// Copyright (c) Ping. All rights reserved.
//
// Pre-auth host for the new-device QR pairing screen. Renders when
// the user picks "Already on another device? Link via QR" on the auth
// welcome screen. Lifecycle:
//
//   1. Generate a fresh X25519 link keypair (pure-JS HPKE). Persist
//      the priv key to sessionStorage so the post-paste fetch+open
//      half can find it.
//   2. Generate an ephemeral SDK identity, wrap it for storage, init
//      the MessagingClient without a transport (we don't need to
//      send anything pre-link).
//   3. Mint a `freshKeyPackage` + read the `deviceId` — these go in
//      the handshake QR.
//   4. On user paste/scan of a ticket_id, fetch the sealed bytes,
//      open with the priv key, hand to `consumeLinkingTicket`, then
//      kick off a full session bootstrap.
//
// Mounted as a route-level component (not a modal) — pairing replaces
// the auth flow until it succeeds. The host owns "back to sign-in"
// via the `onCancel` prop.

import { useCallback, useMemo, useRef } from 'react';
import { Pressable, Text, View } from 'react-native';

import { MessagingClient } from 'ping-openmls-sdk';

import {
  AuthHeader,
  AuthLayout,
  generateKemKeyPair,
  getLinkingTicket,
  type LinkDevicePairingHandshake,
  LinkDevicePairingPane,
  type LinkDeviceSealedPayload,
  LINKING_STREAM_UNAVAILABLE,
  type LinkingStreamHandle,
  linkingStreamUrl,
  pubkeyHashB64Url,
  subscribeLinkingStream,
} from '@ping/ui';
import { identityManager } from '@ping/ui/src/platform/identity.web';
import { PingStorageWeb } from '@ping/ui/src/storage-mls/PingStorage.web';

import { log } from '@core/log/log';
import { linkVerify } from '@core/network/authClient';
import { getDeviceSigningPubkeyBytes } from '@core/network/deviceKeys';
import { setAccessToken } from '@core/network/interceptor';
import { storeIdentity } from '@core/persistence/identity';
import { storeWrappedRefreshToken } from '@core/persistence/tokens';
import type { AuthenticatedUser } from '@features/auth/flow/AuthFlowCoordinator';
import { stashPendingLinkingHistory } from '@features/history/pendingLinkingHistory';

import { QrRenderer } from './QrRenderer';
import { scanQrCode } from './scanQrCode';

const LINK_PRIV_STORAGE_KEY = 'ping.linkingPriv.b64u';

function bytesToUrlBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}


function urlBase64ToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const fill = (4 - (padded.length % 4)) % 4;
  const bin = atob(padded + '='.repeat(fill));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export interface LinkDevicePairingHostProps {
  onCancel: () => void;
  /**
   * Fires after a ticket is successfully consumed AND the BE has
   * issued tokens for this device via `/v1/auth/link-verify`. The
   * payload mirrors the `onRestored` callback on
   * `RecoveryRestoreHost`, so App.tsx can drop both into the same
   * `bootstrapWebSession` path.
   */
  onPaired: (user: AuthenticatedUser) => void;
}

export function LinkDevicePairingHost({
  onCancel,
  onPaired,
}: LinkDevicePairingHostProps): JSX.Element {
  // Pre-auth WS subscription: opened once the handshake is generated
  // and the pubkey_hash is known. The pane resolves
  // `waitForSealedPush` against it; on success we feed the sealed
  // bytes through the same `consumeLinkingTicket` path as the
  // legacy ticket-id paste. A timeout or socket error falls back
  // silently to the ticket-id path the pane already renders.
  const handleRef = useRef<LinkingStreamHandle | null>(null);
  // base64url-encoded SHA-256(eph_pk). Set once during the
  // handshake; consumed by `handleWaitForSealedPush` to derive
  // the WS URL and to drive reconnect retries.
  const pubkeyHashRef = useRef<string | null>(null);
  // The pre-pair SDK client instance built inside
  // `handleGenerateHandshake`. `consumeLinkingTicket` is an INSTANCE
  // method (`MessagingClient.prototype.consumeLinkingTicket`), not a
  // static — calling it on the class via a structural cast +
  // optional chain silently no-ops, which is exactly the bug that
  // made the new device's UI freeze on "Pairing…" after the WS
  // delivered the sealed bytes. Holding the instance here lets both
  // redeem paths (pre-auth WS push + legacy ticket-id) call the
  // method on the right receiver.
  const clientRef = useRef<{
    consumeLinkingTicket(ticket: unknown): Promise<void>;
  } | null>(null);
  // Idempotence guard: React StrictMode double-invokes the pane's
  // mount effect, which calls `onGenerateHandshake` twice. Without
  // a guard each call mints a fresh ephemeral keypair + SDK
  // identity + opens a separate WS to a different pubkey_hash —
  // and only the second matches the QR the user actually sees.
  // The promise cached here lets the second call see the same
  // handshake the first one produced.
  const handshakePromiseRef = useRef<Promise<LinkDevicePairingHandshake> | null>(null);

  // Identity + keypair are generated lazily by the pane via
  // `onGenerateHandshake` so the user sees the screen mount instantly
  // and the spinner inside the pane reflects the SDK init progress.
  //
  // We init the SDK against the SAME `PingStorageWeb` IDB the
  // post-pair `bootstrapWebSession` will use. The earlier "ephemeral
  // in-memory storage" approach was a workaround for a wrap-key
  // collision that's no longer reproducible now that the wrap key
  // is persisted in the IDB itself (see `PingStorageWeb` lines
  // 59-75) — both linking-time inits and post-bootstrap inits read
  // the SAME wrap key, so MLS state written during linking
  // (specifically `consumeLinkingTicket`'s device-group join + the
  // joined-conversation Welcome envelopes from the catchup snapshot)
  // is readable by the bootstrapped SDK on the very next mount.
  // Without this, the linked device would sign in but see zero
  // conversations until the existing device re-invited it.
  //
  // Same identityExport is used here AND by bootstrap (via
  // `identityManager.unwrapIdentity` → `loadOrCreateIdentity`), so
  // there's exactly one SDK identity for the new device end-to-end.
  const handleGenerateHandshake = useCallback(async (): Promise<LinkDevicePairingHandshake> => {
    // StrictMode dedupe: the pane's mount effect fires twice in
    // dev. Without this guard each call mints a fresh ephemeral
    // keypair + SDK identity + opens a separate WS keyed by a
    // different pubkey_hash. The user's QR only matches the
    // second one, so the first WS subscription is wasted bytes
    // and the legacy ticket-id flow ends up the only path.
    if (handshakePromiseRef.current !== null) {
      return handshakePromiseRef.current;
    }
    handshakePromiseRef.current = (async (): Promise<LinkDevicePairingHandshake> => {
    // X25519 ephemeral keypair for HPKE opening of the ticket.
    const kemKeypair = generateKemKeyPair();
    sessionStorage.setItem(LINK_PRIV_STORAGE_KEY, bytesToUrlBase64(kemKeypair.privateKey));

    // Generate a fresh SDK identity. The bytes are CBOR-encoded —
    // `MessagingClient.generateIdentity()` is the canonical factory
    // that matches what the SDK expects on a later `init` /
    // `consumeLinkingTicket`.
    const identityExport = await MessagingClient.generateIdentity();
    await identityManager.wrapIdentity(identityExport);

    // Persistent storage shared with the post-pair bootstrap. See
    // the comment on `handleGenerateHandshake` for why this is the
    // SAME IDB the bootstrapped session uses — `consumeLinkingTicket`
    // writes its device-group MLS state here, and `bootstrapWebSession`
    // opens the same IDB and decrypts it with the same wrap key.
    const storage = new PingStorageWeb();
    const transport = makeNoopTransport();

    const client = (await (MessagingClient as unknown as {
      init: (cfg: {
        identityExport: Uint8Array;
        deviceLabel: string;
        storage: unknown;
        transport: unknown;
      }) => Promise<{
        freshKeyPackage(): Promise<Uint8Array>;
        deviceId(): Promise<Uint8Array>;
        consumeLinkingTicket(ticket: unknown): Promise<void>;
      }>;
    }).init({
      identityExport,
      deviceLabel: 'web-link',
      storage,
      transport,
    })) as {
      freshKeyPackage(): Promise<Uint8Array>;
      deviceId(): Promise<Uint8Array>;
      consumeLinkingTicket(ticket: unknown): Promise<void>;
    };

    // Keep the instance for the redeem step. Both `handleRedeemSealed`
    // and `handleRedeemTicket` need to call `consumeLinkingTicket` on
    // THIS instance, not on `MessagingClient` the class.
    clientRef.current = client;

    const kp = await client.freshKeyPackage();
    const deviceId = await client.deviceId();

    // Stash the pubkey_hash on the host so `handleWaitForSealedPush`
    // can re-subscribe with backoff if the first WS connect fails
    // (transient network, upstream deploy in progress).
    try {
      pubkeyHashRef.current = await pubkeyHashB64Url(kemKeypair.publicKey);
    } catch (err) {
      log.warn({
        event: 'auth.linkDevice.preAuthWs.hash_failed',
        message: (err as Error).message,
      });
    }

    log.info({
      event: 'auth.linkDevice.handshake.ready',
      deviceIdPrefix: bytesToUrlBase64(deviceId).slice(0, 8),
    });

    return {
      deviceIdBase64Url: bytesToUrlBase64(deviceId),
      pubkeyBase64Url: bytesToUrlBase64(kemKeypair.publicKey),
      keyPackageBase64Url: bytesToUrlBase64(kp),
    };
    })();
    return handshakePromiseRef.current;
  }, []);

  const handleWaitForSealedPush = useCallback(async (): Promise<LinkDeviceSealedPayload> => {
    const hash = pubkeyHashRef.current;
    if (hash === null) {
      throw new Error('pre-auth ws: pubkey hash unavailable');
    }
    // Retry loop: when the upstream ws-fanout is briefly
    // unavailable (deploy in progress, transient network blip)
    // we transparently reconnect rather than permanently dropping
    // the auto-pair path. The headline copy in the pane stays on
    // "this device will pair automatically" while this retries
    // in the background. After WS_MAX_TOTAL_MS we give up and
    // reject, which the pane silently treats as "fall back to
    // ticket-id".
    const wsUrl = linkingStreamUrl(window.location.origin, hash);
    const WS_MAX_TOTAL_MS = 10 * 60 * 1000; // 10 minutes
    const WS_BACKOFFS_MS = [500, 1000, 2000, 5000, 10000];
    const deadline = Date.now() + WS_MAX_TOTAL_MS;
    let attempt = 0;
    while (Date.now() < deadline) {
      const handle = subscribeLinkingStream({ url: wsUrl });
      if (handle === LINKING_STREAM_UNAVAILABLE) {
        throw new Error('pre-auth ws: WebSocket not available in this environment');
      }
      // Replace the previous handle (already closed by the failed
      // attempt) so the host's unmount path closes the freshest one.
      handleRef.current?.close();
      handleRef.current = handle;
      try {
        log.info({
          event: 'auth.linkDevice.preAuthWs.opened',
          attempt,
        });
        return await handle.sealed;
      } catch (err) {
        log.warn({
          event: 'auth.linkDevice.preAuthWs.attemptFailed',
          attempt,
          message: (err as Error).message,
        });
        const delay = WS_BACKOFFS_MS[Math.min(attempt, WS_BACKOFFS_MS.length - 1)];
        await new Promise((r) => setTimeout(r, delay));
        attempt += 1;
      }
    }
    throw new Error('pre-auth ws: timed out after retries');
  }, []);

  // Convert ticket fields to the bytes shape `linkVerify` expects.
  // The SDK's `LinkingTicket` exposes user_id / user_pubkey /
  // new_device_id / device_binding_sig as `Bytes` (Uint8Array). Some
  // wire paths can deliver them as plain number[] (serde-wasm-bindgen
  // default), so we accept either shape and normalise.
  const ticketByteField = (v: unknown): Uint8Array => {
    if (v instanceof Uint8Array) return v;
    if (Array.isArray(v)) return new Uint8Array(v);
    throw new Error('linking ticket: malformed byte field');
  };

  // Shared "I just opened a sealed ticket — finish sign-in" path used
  // by both the pre-auth WS push and the legacy ticket-id paste.
  const finishLinkSignIn = useCallback(
    async (payload: LinkDeviceSealedPayload, priv: Uint8Array): Promise<void> => {
      const client = clientRef.current;
      if (client === null) {
        throw new Error('Linking client not initialised — restart the pairing flow.');
      }
      const { sealed, authSealed } = payload;
      if (authSealed.length === 0) {
        // The existing device is on a legacy client that didn't ship
        // the auth envelope — we have no BE-format binding sig to
        // submit to `/v1/auth/link-verify`. Fail with a clear
        // explanation so QA / users know to update the OTHER device
        // rather than chasing a phantom bug on this one.
        throw new Error(
          'The other device is on an older version that does not support '
          + 'one-step linking. Update it and try again.',
        );
      }

      // Use the SDK's wasm-backed `MessagingClient.openLinkingTicket`
      // (static, runs in an ephemeral worker) — same HPKE info /
      // AAD / CBOR envelope as the existing device's
      // `sealLinkingTicket`. The pure-JS reimplementation we had
      // before drifted on the field-key encoding and produced
      // `hpke.open: decryption failed` even with the right keypair.
      const ticket = (await (MessagingClient as unknown as {
        openLinkingTicket: (s: Uint8Array, p: Uint8Array) => Promise<{
          user_id: unknown;
          user_pubkey: unknown;
          new_device_id: unknown;
          device_binding_sig: unknown;
        }>;
      }).openLinkingTicket(sealed, priv)) as {
        user_id: unknown;
        user_pubkey: unknown;
        new_device_id: unknown;
        device_binding_sig: unknown;
      };

      // Open the auth envelope HPKE-sealed against the same eph pub
      // with info `ping/link-auth/v1`. The cleartext carries the
      // existing device's AUTH-LAYER `user_identity_pubkey` + a
      // BE-format `device_binding_sig`. Distinct from the SDK's
      // `device_binding_sig` (which signs over `user_id ||
      // device_id`) — the BE format signs over
      // `"ping/device-binding/v1" || user_identity_pubkey ||
      // device_id` with the AUTH-LAYER user_identity_priv, which is
      // what /v1/auth/link-verify checks against the account's
      // pinned user_identity_pubkey.
      const authInfo = new TextEncoder().encode('ping/link-auth/v1');
      // Open via the SDK's HPKE (same primitive that seals/opens the linking
      // ticket) rather than the pure-JS `hpkeOpen` — the hand-rolled JS/Swift
      // implementations drifted and broke iOS→web (decryption failed) despite
      // identical-looking algorithms. The SDK impl is identical on every
      // platform, so this interops. Must be paired with `hpkeSeal` on the
      // existing device (SettingsModalHost / mobile linking hosts).
      const authClearBytes = await (MessagingClient as unknown as {
        hpkeOpen: (s: Uint8Array, p: Uint8Array, info: Uint8Array) => Promise<Uint8Array>;
      }).hpkeOpen(authSealed, priv, authInfo);
      const authClear = JSON.parse(new TextDecoder().decode(authClearBytes)) as {
        v: number;
        user_pubkey: string;
        binding_sig: string;
        // v2 inviters transfer their SDK account identity seed (CBOR-wrapped
        // Ed25519, base64url) so both devices share one MLS user_id.
        identity_seed?: string;
      };
      // v1 = legacy (no seed); v2 = shared-identity inviter. Accept both.
      if (authClear.v < 1) {
        throw new Error(`auth envelope: unsupported version ${authClear.v}`);
      }

      // Shared-identity linking: ADOPT the inviter's account identity by
      // re-wrapping it over the throwaway identity minted at handshake. The
      // bootstrap (`bootstrapWebSession` → `identityManager.unwrapIdentity`)
      // then opens the already-written device-group MLS state under the SHARED
      // identity — the device signing key in IDB is unchanged so `deviceId`
      // stays stable, only the account-level credential user_id is unified.
      // After this, a sibling device's message arrives with
      // `sender_user_id === client.userId()` and renders as self. v1 inviters
      // omit the seed; the new device keeps its own identity (device→account
      // mapping fallback).
      if (typeof authClear.identity_seed === 'string' && authClear.identity_seed.length > 0) {
        const sharedIdentity = urlBase64ToBytes(authClear.identity_seed);
        if (sharedIdentity.length > 0) {
          await identityManager.wrapIdentity(sharedIdentity);
          log.info({ event: 'auth.linkDevice.sharedIdentity.adopted' });
        }
      }

      // Ensure the new device has a local Ed25519 signing keypair —
      // its pubkey goes to the BE on link-verify so JWT/binding ops
      // afterwards can verify against the right key. `loadOrCreateKeyPair`
      // is single-flight cached so this is cheap on repeat calls.
      const deviceSigningPubkey = await getDeviceSigningPubkeyBytes();

      // OTP-less sign-in: BE verifies the BE-format
      // `device_binding_sig` against the account's pinned
      // `user_identity_pubkey` and mints tokens. See
      // services/api/internal/auth/service.go::LinkVerify.
      const verify = await linkVerify({
        userPubkeyBytes: urlBase64ToBytes(authClear.user_pubkey),
        newDeviceIdBytes: ticketByteField(ticket.new_device_id),
        deviceBindingSigBytes: urlBase64ToBytes(authClear.binding_sig),
        deviceSigningPubkeyBytes: deviceSigningPubkey,
      });

      // Persist tokens + identity the same way the OTP-verify path
      // does so the subsequent `bootstrapWebSession` call in App.tsx
      // can pick them up via the same retrieval helpers (silent reauth,
      // refresh interceptor, etc.).
      setAccessToken(verify.accessToken, verify.expiresIn);
      await Promise.all([
        storeWrappedRefreshToken(verify.refreshToken),
        storeIdentity({
          accountId: verify.accountId,
          deviceId: verify.deviceId,
          // Display name isn't carried by the LinkingTicket — bootstrap
          // will fetch it from `GET /v1/me/profile` and storeIdentity it
          // again with the canonical value.
          displayName: '',
        }),
      ]);

      // Mirror the AUTH-LAYER user_identity_pubkey we just verified
      // into the local `__user_identity_key` settings_kv slot so
      // subsequent flows that read it (e.g.
      // `getUserIdentityPubkeyBytes` for sending a sub-link to a
      // FUTURE new device) see the right pubkey. We do NOT have the
      // matching priv (that's the whole point of the link-verify
      // design — no priv-key transfer), so cryptographic ops that
      // need the priv (sign_device_binding, exportUserIdentityForBackup)
      // will fail until the user signs in via OTP or recovery. That's
      // an acceptable limitation for a linked device.
      try {
        const { db } = await import('@core/persistence/db');
        await db.table('settings_kv').put({
          key: '__user_identity_key',
          value: {
            publicKeyBytes: Array.from(urlBase64ToBytes(authClear.user_pubkey)),
            // No private key — see comment above.
          },
          updatedAt: Date.now(),
        });
      } catch (kvErr) {
        log.warn({
          event: 'auth.linkDevice.userIdentityKey.persistFailed',
          message: (kvErr as Error).message,
        });
      }

      // Hand the ticket to the SDK. With PingStorageWeb wired in
      // (see comment on `handleGenerateHandshake`), the device-group
      // MLS state lands in the same IDB the bootstrapped session
      // will reopen — so the linked device sees existing chats
      // without a re-invite.
      try {
        await client.consumeLinkingTicket(ticket);
      } catch (consumeErr) {
        log.warn({
          event: 'auth.linkDevice.consume.failed',
          message: (consumeErr as Error).message,
        });
      }

      // NOTE: we deliberately skip `MessagingClient.decodeCatchupSnapshot` +
      // `client.importStateSnapshot` here. That path used to import
      // MLS state for each chat locally, but it creates a chicken-
      // and-egg with the BE: the BE's `auth.conversation_members`
      // table still records only the inviting device for each chat,
      // so the new device's first `fetchSince` 404s and the chat is
      // orphan-pruned. Worse — when the existing device's
      // post-link reconciler eventually issues `addMembers` for
      // each chat, the matching Welcome envelope arrives at the new
      // device but `dispatchIncoming` drops it as a duplicate
      // (`listConversations` already contains the id from the
      // earlier import), so the BE-recorded membership never lands
      // on this side either.
      //
      // The right flow:
      //   1. Linking ticket only joins the DG (handled by
      //      `consumeLinkingTicket` above).
      //   2. Existing device's `reconcileChatMembershipForLinkedDevice`
      //      (see SettingsModalHost.tsx) waits for THIS device to
      //      finish bootstrap + KP-replenish, then calls
      //      `client.addMembers` once per chat. Each call sends a
      //      Commit (updates BE membership) AND a Welcome routed
      //      to THIS device's WS. The standard
      //      `useIncomingConversations` hook then seeds the Dexie
      //      chat row when `onConversationUpdated` fires on the
      //      Welcome arrival.
      //
      // The chats therefore appear in the chat list a few seconds
      // after the user lands on the AppShell — not instantaneously,
      // but consistently and without the orphan-prune race.

      // [#2] Stash the keys this device needs to OPEN the history the
      // existing device is about to ship: our ephemeral X25519 PRIV (the QR
      // one, `priv`) decrypts the HPKE seal, and the account user-identity
      // PUBKEY (just verified, `authClear.user_pubkey`) authenticates the
      // sender's signature. The existing device posts the sealed bundle a
      // few seconds after it sees this device link-verify, so a post-
      // bootstrap poller in App.tsx (`consumePendingLinkingHistory`) drains
      // it. Copy `priv` — the SDK worker may reuse/zero the original buffer.
      stashPendingLinkingHistory({
        ephemeralPriv: priv.slice(),
        senderIdentityPub: urlBase64ToBytes(authClear.user_pubkey),
      });

      sessionStorage.removeItem(LINK_PRIV_STORAGE_KEY);
      log.info({ event: 'auth.linkDevice.paired' });

      const user: AuthenticatedUser = {
        tokens: { hasAccessToken: true },
        identity: {
          accountId: verify.accountId,
          deviceId: verify.deviceId,
          displayName: '',
        },
      };
      onPaired(user);
    },
    [onPaired],
  );

  const handleRedeemSealed = useCallback(
    async (payload: LinkDeviceSealedPayload): Promise<void> => {
      const privB64Url = sessionStorage.getItem(LINK_PRIV_STORAGE_KEY);
      if (!privB64Url) throw new Error('Linking private key missing — restart the pairing flow.');
      await finishLinkSignIn(payload, urlBase64ToBytes(privB64Url));
    },
    [finishLinkSignIn],
  );

  const handleRedeemTicket = useCallback(
    async (ticketId: string): Promise<void> => {
      const privB64Url = sessionStorage.getItem(LINK_PRIV_STORAGE_KEY);
      if (!privB64Url) throw new Error('Linking private key missing — restart the pairing flow.');
      const sealedResp = await getLinkingTicket(ticketId);
      const payload: LinkDeviceSealedPayload = {
        sealed: urlBase64ToBytes(sealedResp.sealed),
        authSealed:
          sealedResp.auth_sealed && sealedResp.auth_sealed.length > 0
            ? urlBase64ToBytes(sealedResp.auth_sealed)
            : new Uint8Array(0),
      };
      await finishLinkSignIn(payload, urlBase64ToBytes(privB64Url));
    },
    [finishLinkSignIn],
  );

  // Pin the renderer + scanner so identity references stay stable —
  // avoids re-running the `useEffect` inside the pane that generates
  // the handshake.
  const renderQrCode = useMemo(
    () =>
      (value: string, size: number): JSX.Element => (
        <QrRenderer value={value} size={size} />
      ),
    [],
  );

  return (
    <AuthLayout accessibilityLabel="link-device">
      <AuthHeader
        title="Link this device"
        subtitle="Pair using your other Ping device — end-to-end encrypted."
      />
      <LinkDevicePairingPane
        onGenerateHandshake={handleGenerateHandshake}
        onRedeemTicket={handleRedeemTicket}
        waitForSealedPush={handleWaitForSealedPush}
        onRedeemSealed={handleRedeemSealed}
        renderQrCode={renderQrCode}
        onScanRequest={scanQrCode}
        // `onPairingComplete` previously wired to `onCancel`, which
        // fired the destructive `goUnauthenticated('linkDevice.cancel')`
        // wipe right after a SUCCESSFUL link — destroying the freshly
        // imported SDK state. The host's redeem callbacks
        // (`handleRedeemSealed` / `handleRedeemTicket`) already call
        // `onPaired` after `consumeLinkingTicket`, which pops the
        // parent route correctly. So this prop is genuinely redundant
        // and the safe wire is "omit it entirely".
      />
      <View className="items-center mt-xl">
        <Pressable
          onPress={onCancel}
          accessibilityRole="link"
          accessibilityLabel="ping_link_back_to_signin"
        >
          <Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-secondary dark:text-dark-text-secondary underline">
            Back to sign in
          </Text>
        </Pressable>
      </View>
    </AuthLayout>
  );
}

// No-op transport: the SDK's `init` requires a transport but the
// pre-link pairing flow never sends anything live. All four methods
// reject so any accidental call surfaces immediately rather than
// silently dropping data.
function makeNoopTransport(): unknown {
  const reject = (name: string) => () =>
    Promise.reject(new Error(`linking-noop-transport: ${name} not available pre-pairing`));
  return {
    send: reject('send'),
    fetchSince: reject('fetchSince'),
    discoverDevices: reject('discoverDevices'),
    subscribe: () => ({ unsubscribe: () => {} }),
  };
}

// `makeEphemeralStorage` removed — the linking SDK now initialises
// against `PingStorageWeb` so the device-group MLS state from
// `consumeLinkingTicket` is persisted into the same IDB the
// post-pair bootstrap reopens. See the comment on
// `handleGenerateHandshake` for the wrap-key compatibility note.
